import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

interface LoadingProgressBarProps {
  /** Duration in seconds for the progress bar to complete */
  durationInSeconds: number;
  /** Whether the progress bar is active/running */
  isActive: boolean;

  /** Optional custom class for the container */
  className?: string;
  /** Optional custom class for the progress bar */
  barClassName?: string;
  /** Height of the progress bar (default: "h-2") */
  height?: string;
}

export const LoadingProgressBar: React.FC<LoadingProgressBarProps> = ({
  durationInSeconds,
  isActive,
  className,
  barClassName,
  height = "h-1",
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setProgress(0);
      return;
    }

    const totalMs = durationInSeconds * 1000;
    const intervalMs = 50; // Update every 50ms for smooth animation
    const increment = (intervalMs / totalMs) * 100;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(interval);
          return 100;
        }
        return next;
      });
    }, intervalMs);

    return () => {
      clearInterval(interval);
    };
  }, [isActive, durationInSeconds]);

  // Reset progress when isActive changes to true
  useEffect(() => {
    if (isActive) {
      setProgress(0);
    }
  }, [isActive]);

  return (
    <div className={twMerge("w-full", className)}>
      <div className={twMerge("w-full bg-gray-200 rounded-full overflow-hidden", height)}>
        <div
          className={twMerge("h-full bg-green-500 rounded-full transition-all duration-100 ease-linear", barClassName)}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};
